import React, { useMemo } from 'react';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { Box, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AcceptanceDemoBadge,
  AcceptanceDemoButton,
  AcceptanceDemoIconContainer,
  AcceptanceDemoPageShell,
  AcceptanceDemoPanel,
  AcceptanceDemoSectionHeader,
  AcceptanceDemoStatCard,
  acceptanceDemoTokens,
} from '../components/overview/acceptanceDemoDesignSystem';
import {
  DEFAULT_WEB_SUMMIT_SCENARIO_ID,
  buildAcceptanceScenarioProofPath,
  buildAcceptanceScenarioReviewPath,
  getWebSummitDemoScenarioById,
} from './demo/webSummitDemoScenarios';

const AcceptanceImpactScenarios: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scenario = useMemo(
    () => getWebSummitDemoScenarioById(DEFAULT_WEB_SUMMIT_SCENARIO_ID),
    [],
  );
  const reviewPath = useMemo(
    () => buildAcceptanceScenarioReviewPath(scenario.id, location.search),
    [location.search, scenario.id],
  );
  const safetyReportPath = useMemo(
    () => buildAcceptanceScenarioProofPath(scenario.id, location.search),
    [location.search, scenario.id],
  );
  const liveViewPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.set('scenarioId', scenario.id);
    params.set('scenario_id', scenario.id);
    params.set('site', scenario.siteId);
    params.set('site_id', scenario.siteId);
    params.set('cameraSource', scenario.cameraSource);
    params.set('camera_source', scenario.cameraSource);
    const query = params.toString();
    return query ? `/live-view?${query}` : '/live-view';
  }, [location.search, scenario.cameraSource, scenario.id, scenario.siteId]);

  const demoProofPoints = [
    {
      title: 'Camera event',
      detail: 'Pedestrian activity in a forklift operating area is detected from the live feed.',
      icon: <CameraAltOutlinedIcon fontSize="small" />,
      accent: 'orange' as const,
      ctaLabel: 'Open Live View',
      onClick: () => navigate(liveViewPath),
    },
    {
      title: 'Recurring risk',
      detail: 'Related camera events roll up into one recurring safety risk for human review.',
      icon: <AssignmentOutlinedIcon fontSize="small" />,
      accent: 'blue' as const,
      ctaLabel: 'Open Review',
      onClick: () => navigate(reviewPath),
    },
    {
      title: 'Event records',
      detail: 'Clips and context stay grouped under the recurring risk without manual assembly.',
      icon: <RuleFolderOutlinedIcon fontSize="small" />,
      accent: 'blue' as const,
      ctaLabel: 'Start review',
      onClick: () => navigate(reviewPath),
    },
    {
      title: 'Follow-up action',
      detail: 'Assign ownership, track corrective action, and prepare the Safety Report.',
      icon: <ChecklistRoundedIcon fontSize="small" />,
      accent: 'orange' as const,
      ctaLabel: 'Open Safety Report',
      onClick: () => navigate(safetyReportPath),
    },
    {
      title: 'Measurement',
      detail: 'The same safety rule and area can be tracked after deployment to measure recurrence.',
      icon: <TimelineRoundedIcon fontSize="small" />,
      accent: 'blue' as const,
      ctaLabel: 'View Measurement Framework',
      onClick: () => {
        if (typeof document === 'undefined') return;
        document.getElementById('impact-story')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    },
  ];

  const safetyImpactPoints = [
    'Detect pedestrian activity in a forklift operating area',
    'Convert the event into a reviewable incident',
    'Capture evidence needed for follow-up',
    'Create the operational path to measure recurrence after action',
    'Track the same safety rule and area over time after deployment',
  ];

  const businessValuePoints = [
    'Faster incident review',
    'Less manual evidence assembly',
    'Clearer follow-up ownership',
    'Better audit and customer reporting readiness',
    'Baseline for future impact measurement',
  ];

  const demoLoopSteps = [
    'Camera event',
    'Recurring risk',
    'Human review',
    'Safety Report',
    'Measurement',
  ];

  const deploymentMetrics = [
    'Baseline recurrence rate',
    'Post-action recurrence rate',
    'Observed reduction after action',
    'Review time saved',
    'Reporting time saved',
    'Estimated business value',
  ];

  const measurementSteps = [
    'Establish baseline recurrence rate',
    'Complete follow-up action',
    'Monitor the post-action window',
    'Compare recurrence rates',
    'Estimate time saved and business value using customer assumptions',
  ];

  const scenarioRows = [
    {
      title: 'Recurring safety events',
      currentValue: '6,384',
      projectedValue: '4,469',
      currentWidth: '100%',
      projectedWidth: '70%',
      metricChip: '30% assumed reduction',
      detailLabel: 'Assumed reduction',
      detailValue: '30%',
      currentHelper: 'baseline recurrence count',
      projectedHelper: 'illustrative scenario value',
    },
    {
      title: 'Manual review effort',
      currentValue: '213 hours',
      projectedValue: '149 hours',
      currentWidth: '100%',
      projectedWidth: '70%',
      metricChip: '30% assumed reduction',
      detailLabel: 'Assumed reduction',
      detailValue: '30%',
      currentHelper: 'baseline estimate',
      projectedHelper: 'illustrative scenario value',
    },
    {
      title: 'Estimated operational cost',
      currentValue: '$63,840',
      projectedValue: '$44,690',
      currentWidth: '100%',
      projectedWidth: '70%',
      metricChip: 'Estimated value',
      detailLabel: 'Estimated value',
      detailValue: '$19,150',
      supportingNote: 'Assumption: $10 per recurring safety event',
      currentHelper: 'baseline estimate',
      projectedHelper: 'illustrative scenario value',
      detailHelper: 'estimated from assumptions',
    },
  ];

  return (
    <AcceptanceDemoPageShell>
      <AcceptanceDemoPanel tone="hero" sx={{ p: { xs: 2.1, md: 2.6 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.1fr) minmax(320px, 0.9fr)' },
            gap: { xs: 1.8, xl: 2.4 },
            alignItems: 'start',
          }}
        >
          <Stack spacing={1}>
            <Typography
              variant="overline"
              sx={{ color: acceptanceDemoTokens.colors.orangeSoft, fontWeight: 900, letterSpacing: 1.4 }}
            >
              WEBSUMMIT SHOWCASE
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.045,
                lineHeight: 1.02,
                fontSize: { xs: '2.25rem', md: '3.4rem' },
                maxWidth: 760,
              }}
            >
              From unsafe activity to reviewable safety action.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: acceptanceDemoTokens.colors.textSecondary,
                fontWeight: 500,
                lineHeight: 1.5,
                maxWidth: 760,
                fontSize: { xs: '1rem', md: '1.04rem' },
              }}
            >
              Edgentik detects pedestrian activity in a forklift operating area, captures the evidence, and turns it
              into a reviewable safety workflow.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: acceptanceDemoTokens.colors.textMuted, lineHeight: 1.6, maxWidth: 760 }}
            >
              This WebSummit demo shows workflow readiness: detection, evidence capture, review, Safety Report
              generation, and follow-up readiness. Measured recurrence reduction comes after deployment.
            </Typography>
            <AcceptanceDemoPanel tone="subtle" sx={{ p: 0.95, mt: 0.15 }}>
              <Stack spacing={0.8}>
                <Typography variant="overline" sx={{ color: acceptanceDemoTokens.colors.blueSoft, fontWeight: 800, letterSpacing: 1 }}>
                  Demo Loop
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' },
                    gap: 0.8,
                    alignItems: 'center',
                  }}
                >
                  {demoLoopSteps.map((step, index) => (
                    <Box
                      key={step}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                      }}
                    >
                      <AcceptanceDemoPanel
                        tone={index === demoLoopSteps.length - 1 ? 'orange' : 'blue'}
                        sx={{ p: 0.95, width: '100%', minHeight: 78, display: 'flex', alignItems: 'center' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.4 }}>
                          {step}
                        </Typography>
                      </AcceptanceDemoPanel>
                      {index < demoLoopSteps.length - 1 ? (
                        <Typography
                          variant="body2"
                          sx={{
                            display: { xs: 'none', md: 'block' },
                            color: acceptanceDemoTokens.colors.textMuted,
                            fontWeight: 900,
                          }}
                        >
                          →
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Stack>
            </AcceptanceDemoPanel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ pt: 0.15 }}>
              <AcceptanceDemoButton label="Review Incident" onClick={() => navigate(reviewPath)} accent="orange" />
              <AcceptanceDemoButton
                label="Open Safety Report"
                onClick={() => navigate(safetyReportPath)}
                accent="blue"
                variant="outlined"
              />
            </Stack>
          </Stack>

          <AcceptanceDemoPanel tone="subtle" sx={{ p: { xs: 1.05, md: 1.2 } }}>
            <Stack spacing={0.75}>
              <Typography variant="overline" sx={{ color: acceptanceDemoTokens.colors.blueSoft, fontWeight: 800, letterSpacing: 1 }}>
                Demo Context
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <AcceptanceDemoBadge label={scenario.cameraLabel} accent="blue" icon={<CameraAltOutlinedIcon fontSize="small" />} />
                <AcceptanceDemoBadge label="Forklift operating area" accent="orange" icon={<RuleFolderOutlinedIcon fontSize="small" />} />
                <AcceptanceDemoBadge label="Pedestrian in forklift area" accent="blue" icon={<InsightsOutlinedIcon fontSize="small" />} />
              </Stack>
              <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
                Showcase demo, not measured deployment results.
              </Typography>
            </Stack>
          </AcceptanceDemoPanel>
        </Box>
      </AcceptanceDemoPanel>

      <AcceptanceDemoPanel tone="subtle" sx={{ p: { xs: 2, md: 2.35 } }}>
        <AcceptanceDemoSectionHeader
          eyebrow="Section 1"
          title="What the demo shows"
          description="This demo shows how Edgentik converts a live safety signal into an operational workflow. It does not claim that recurrence has already been reduced; that requires follow-up action and post-action measurement."
        />
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(auto-fit, minmax(220px, 1fr))',
            },
            gap: 1.2,
            alignItems: 'stretch',
          }}
        >
          {demoProofPoints.map((point) => (
            <AcceptanceDemoStatCard
              key={point.title}
              title={point.title}
              detail={point.detail}
              accent={point.accent}
              icon={point.icon}
              ctaLabel={point.ctaLabel}
              onClick={point.onClick}
              minHeight={{ xs: 168, md: 'auto' }}
            />
          ))}
        </Box>
      </AcceptanceDemoPanel>

      <Box
        id="impact-story"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <AcceptanceDemoPanel tone="blue" sx={{ p: { xs: 2, md: 2.3 } }}>
          <Stack spacing={1.2}>
            <Typography variant="overline" sx={{ color: acceptanceDemoTokens.colors.blueSoft, fontWeight: 800, letterSpacing: 1 }}>
              Section 2
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: -0.03 }}>
              Safety Impact
            </Typography>
            <Typography variant="body1" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.65 }}>
              The demo shows how a pedestrian-in-forklift-area event can be detected, reviewed, and prepared for
              follow-up.
            </Typography>
            <Stack spacing={1}>
              {safetyImpactPoints.map((point) => (
                <Stack key={point} direction="row" spacing={1.1} alignItems="flex-start">
                  <AcceptanceDemoIconContainer accent="blue" sx={{ width: 30, height: 30, mt: 0.1 }}>
                    <CheckIcon />
                  </AcceptanceDemoIconContainer>
                  <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textPrimary, lineHeight: 1.6 }}>
                    {point}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <AcceptanceDemoPanel tone="subtle" sx={{ p: 1.3 }}>
              <Stack spacing={0.9}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  What the demo proves
                </Typography>
                <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
                  Detection, evidence capture, review readiness, Safety Report generation, and follow-up readiness.
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, pt: 0.35 }}>
                  What deployment measures
                </Typography>
                <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
                  Recurrence reduction, review time saved, reporting time saved, and estimated business value.
                </Typography>
              </Stack>
            </AcceptanceDemoPanel>
          </Stack>
        </AcceptanceDemoPanel>

        <AcceptanceDemoPanel tone="orange" sx={{ p: { xs: 2, md: 2.3 } }}>
          <Stack spacing={1.2}>
            <Typography variant="overline" sx={{ color: acceptanceDemoTokens.colors.orangeSoft, fontWeight: 800, letterSpacing: 1 }}>
              Section 3
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: -0.03 }}>
              Business Value
            </Typography>
            <Typography variant="body1" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.65 }}>
              Edgentik reduces the manual work between detection and resolution: finding evidence, creating a
              reviewable case, assigning follow-up, and preparing reporting material.
            </Typography>
            <Stack spacing={1}>
              {businessValuePoints.map((point) => (
                <Stack key={point} direction="row" spacing={1.1} alignItems="flex-start">
                  <AcceptanceDemoIconContainer accent="orange" sx={{ width: 30, height: 30, mt: 0.1 }}>
                    <ValueIcon />
                  </AcceptanceDemoIconContainer>
                  <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textPrimary, lineHeight: 1.6 }}>
                    {point}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <AcceptanceDemoPanel tone="subtle" sx={{ p: 1.3 }}>
              <Stack spacing={0.9}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  What the demo proves today
                </Typography>
                <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
                  Workflow efficiency and proof readiness.
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, pt: 0.35 }}>
                  What deployment can quantify later
                </Typography>
                <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
                  Recurrence reduction, time saved, avoided manual review effort, and estimated financial value.
                </Typography>
              </Stack>
            </AcceptanceDemoPanel>
          </Stack>
        </AcceptanceDemoPanel>
      </Box>

      <Box id="impact-framework" sx={{ scrollMarginTop: 24 }}>
        <AcceptanceDemoPanel tone="subtle">
          <AcceptanceDemoSectionHeader
            eyebrow="Section 4"
            title="What customers measure after deployment"
            description="After deployment, Edgentik can measure whether the same safety rule and area improves over time."
          />
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: '1.2fr 0.8fr' },
              gap: 2,
            }}
          >
            <AcceptanceDemoPanel tone="default" sx={{ p: 1.45 }}>
              <Stack spacing={1.15}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  How deployment measurement works
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  {measurementSteps.map((step, index) => (
                    <Box key={step} sx={{ display: 'grid', gap: 0.65 }}>
                      <AcceptanceDemoPanel tone={index === 2 ? 'orange' : 'blue'} sx={{ p: 0.95, minHeight: 112 }}>
                        <Stack spacing={0.8}>
                          <Typography variant="overline" sx={{ color: acceptanceDemoTokens.colors.textMuted, fontWeight: 800, letterSpacing: 1 }}>
                            Step {index + 1}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.5 }}>
                            {step}
                          </Typography>
                        </Stack>
                      </AcceptanceDemoPanel>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </AcceptanceDemoPanel>

            <AcceptanceDemoPanel tone="default" sx={{ p: 1.45 }}>
              <Stack spacing={0.95}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Deployment metrics
                </Typography>
                {deploymentMetrics.map((metric) => (
                  <Stack key={metric} direction="row" spacing={1} alignItems="center">
                    <AcceptanceDemoIconContainer accent="blue" sx={{ width: 28, height: 28 }}>
                      <QueryStatsRoundedIcon fontSize="small" />
                    </AcceptanceDemoIconContainer>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {metric}
                    </Typography>
                  </Stack>
                ))}
                <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textMuted, lineHeight: 1.6, pt: 0.6 }}>
                  Use observed reduction after action, not reduction caused by action.
                </Typography>
              </Stack>
            </AcceptanceDemoPanel>
          </Box>
        </AcceptanceDemoPanel>
      </Box>

      <AcceptanceDemoPanel tone="hero" sx={{ p: { xs: 2, md: 2.35 } }}>
        <Stack spacing={1.1}>
          <Typography variant="overline" sx={{ color: acceptanceDemoTokens.colors.orangeSoft, fontWeight: 800, letterSpacing: 1 }}>
            Section 5
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: -0.03 }}>
            Potential Business Impact Scenario
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
            <AcceptanceDemoBadge label="Illustrative projection" accent="orange" />
            <AcceptanceDemoBadge label="Scenario model" accent="blue" />
          </Stack>
          <Typography variant="subtitle1" sx={{ color: acceptanceDemoTokens.colors.orangeSoft, fontWeight: 800 }}>
            Illustrative projection — not measured deployment results.
          </Typography>
          <Typography variant="body1" sx={{ color: acceptanceDemoTokens.colors.textSecondary, maxWidth: 920, lineHeight: 1.65 }}>
            This scenario shows how Edgentik can translate measured recurrence change into estimated business value
            after deployment. The values below are based on configurable assumptions, not results from this demo.
          </Typography>
          <AcceptanceDemoPanel tone="orange" sx={{ p: 1.2 }}>
            <Stack spacing={0.45}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Scenario model only
              </Typography>
              <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
                These numbers are illustrative. They are not measured results from this demo.
              </Typography>
            </Stack>
          </AcceptanceDemoPanel>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: '1.25fr 0.75fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <AcceptanceDemoPanel tone="default" sx={{ p: 1.45 }}>
              <Stack spacing={1.15}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <AcceptanceDemoBadge label="Current baseline" accent="neutral" />
                  <AcceptanceDemoBadge label="Illustrative after follow-up" accent="blue" />
                </Stack>
                {scenarioRows.map((row) => (
                  <AcceptanceDemoPanel key={row.title} tone="subtle" sx={{ p: 1.1 }}>
                    <Stack spacing={0.85}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.8}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {row.title}
                        </Typography>
                        <AcceptanceDemoBadge label={row.metricChip} accent="orange" />
                      </Stack>
                      <Stack spacing={0.65}>
                        <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textMuted, fontWeight: 700 }}>
                          Current baseline
                        </Typography>
                        <Box
                          sx={{
                            height: 14,
                            width: '100%',
                            borderRadius: 999,
                            background: 'rgba(255,255,255,0.08)',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: row.currentWidth,
                              borderRadius: 999,
                              background: `linear-gradient(90deg, ${acceptanceDemoTokens.colors.orange} 0%, ${acceptanceDemoTokens.colors.orangeSoft} 100%)`,
                            }}
                          />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 850, letterSpacing: -0.02 }}>
                          {row.currentValue}
                        </Typography>
                        <Typography variant="caption" sx={{ color: acceptanceDemoTokens.colors.textMuted, lineHeight: 1.45 }}>
                          {row.currentHelper}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.65}>
                        <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textMuted, fontWeight: 700 }}>
                          Illustrative after follow-up
                        </Typography>
                        <Box
                          sx={{
                            height: 14,
                            width: '100%',
                            borderRadius: 999,
                            background: 'rgba(255,255,255,0.08)',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: row.projectedWidth,
                              borderRadius: 999,
                              background: `linear-gradient(90deg, ${acceptanceDemoTokens.colors.blue} 0%, ${acceptanceDemoTokens.colors.blueSoft} 100%)`,
                            }}
                          />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 850, letterSpacing: -0.02 }}>
                          {row.projectedValue}
                        </Typography>
                        <Typography variant="caption" sx={{ color: acceptanceDemoTokens.colors.textMuted, lineHeight: 1.45 }}>
                          {row.projectedHelper}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.55 }}>
                        <Box component="span" sx={{ fontWeight: 800 }}>
                          {row.detailLabel}:
                        </Box>{' '}
                        {row.detailValue}
                      </Typography>
                      {row.detailHelper ? (
                        <Typography variant="caption" sx={{ color: acceptanceDemoTokens.colors.textMuted, lineHeight: 1.45 }}>
                          {row.detailHelper}
                        </Typography>
                      ) : null}
                      {row.supportingNote ? (
                        <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textMuted, lineHeight: 1.55 }}>
                          {row.supportingNote}
                        </Typography>
                      ) : null}
                    </Stack>
                  </AcceptanceDemoPanel>
                ))}
              </Stack>
            </AcceptanceDemoPanel>

            <AcceptanceDemoPanel tone="default" sx={{ p: 1.45 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Assumptions used in this scenario
                </Typography>
                <Stack spacing={0.7}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Baseline recurrence: 6,384 related alerts/events
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Assumed post-follow-up reduction: 30%
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Cost assumption: $10 per recurring safety event
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Review-time assumption: 2 minutes per reviewed event
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Financial values: Estimated from customer assumptions
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Measurement status: Not measured in this demo
                  </Typography>
                </Stack>
                <AcceptanceDemoPanel tone="subtle" sx={{ p: 1.1 }}>
                  <Stack spacing={0.7}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Confidence labels
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      Baseline recurrence: Evidence-backed
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      Assumed reduction: Estimated from customer assumptions
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      Cost per event: Estimated from customer assumptions
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      Estimated value: Estimated from customer assumptions
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      Deployment result: Measurement pending
                    </Typography>
                  </Stack>
                </AcceptanceDemoPanel>
                <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textSecondary, lineHeight: 1.6 }}>
                  A live deployment would replace the assumed reduction with measured post-action recurrence data for
                  the same safety rule and area.
                </Typography>
              </Stack>
            </AcceptanceDemoPanel>
          </Box>
          <Typography variant="body2" sx={{ color: acceptanceDemoTokens.colors.textMuted, lineHeight: 1.65 }}>
            This scenario is illustrative. A live deployment replaces assumptions with measured recurrence data from
            the same safety rule and area.
          </Typography>
        </Stack>
      </AcceptanceDemoPanel>
    </AcceptanceDemoPageShell>
  );
};

const CheckIcon: React.FC = () => <TimelineRoundedIcon fontSize="small" />;
const ValueIcon: React.FC = () => <SavingsOutlinedIcon fontSize="small" />;

export default AcceptanceImpactScenarios;
